import { useTheme } from "@mui/material";
import type { Cancelable, NativeEventInfo } from "devextreme/events";
import type dxChart from "devextreme/viz/chart";
import type { ValueAxis as DxValueAxis } from "devextreme/viz/chart";
import type { JSX } from "react";
import { cloneElement, useCallback } from "react";
import type { ChartStyleSettings, PivotRowValueCell, ReportField } from "../../../../../../../api/types/svTypes.ts";
import { formatMeasure } from "../../../formatters/NumberFormatter.ts";
import { hasAvailableDrillDown } from "../common/hasAvailableDrillDown.ts";
import "./ChartCore.css";
import type { ChartProps } from "./ChartProps.ts";
import { getLinearSettings } from "./helper.ts";
import { isRotated } from "./isRotated.ts";
import { argumentAxisTitle } from "./style/argumentAxis/argumentAxisTitle.ts";
import { isShowArgumentLabel } from "./style/argumentAxis/isShowArgumentLabel.ts";
import formatArgumentLabel from "./utils/argumentFormat.ts";
import { getArgumentAxisGrid, getValueAxisGrid } from "./utils/axisGrid.ts";
import updateValueAxis from "./utils/updateValueAxis.ts";
interface Props extends ChartProps {
  children: JSX.Element;
}

// This interface from "devextreme/viz/chart_components/base_chart" is marked as deprecated in DX ("for internal purposes only")
interface PointInteractionInfo {
  target: {
    originalValue?: string | number | Date;
    series: {
      name: string;
    };
  };
}

export function ChartCore({ children, args, values, size, data, onDrillDown, settings }: Props) {
  const { palette } = useTheme();

  const handlePointDrillDown = useCallback(
    (serieName: string, value: unknown) => {
      const cell = data.find((row) => row[serieName]?.value === value);
      if (cell !== undefined) {
        const measureCell = cell[serieName] as PivotRowValueCell;
        if (hasAvailableDrillDown(measureCell?.drillDownId)) {
          const measure = values.find((f) => f.config.guid === serieName);
          if (measure === undefined) return;
          onDrillDown?.(measure, measureCell);
        }
      }
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [values, args, onDrillDown]
  );

  return cloneElement(children, {
    palette: [
      "#00A866",
      "#3395A8",
      "#2FCE90",
      "#6AD4FD",
      "#34B8E2",
      "#147DDB",
      "#FFCB66",
      "#FF6D38",
      "#DD391B",
      "#6829A7",
    ],
    size,
    className: "entr-chart-core",
    rotated: isRotated(settings),
    resolveLabelOverlapping: "hide",
    tooltip: buildToolTip(values, settings),
    argumentAxis: {
      color: palette.divider,
      tick: { color: palette.divider },
      grid: { ...getArgumentAxisGrid(settings), color: palette.divider },
      label: {
        visible: isShowArgumentLabel(settings),
        customizeText: (info: { value: string }) => formatArgumentLabel(args, info.value, data),
        font: { family: "Roboto" },
      },
      title: { text: argumentAxisTitle(settings), font: { family: "Roboto" } },
      tickInterval: 1,
    },
    valueAxis: defaultValueAxis(settings).map((axis) => ({
      ...axis,
      color: palette.divider,
      tick: { color: palette.divider },
      grid: { ...axis.grid, color: palette.divider },
    })),
    onPointClick: (e: NativeEventInfo<dxChart, MouseEvent | PointerEvent> & PointInteractionInfo) => {
      const cancellable = e.event as Cancelable;
      cancellable.cancel = true;
      const value = e.target.originalValue;
      const serieName = e.target.series.name;
      handlePointDrillDown(serieName, value);
    },
  });
}

function buildToolTip(values: ReportField[], settings: ChartStyleSettings) {
  const contentTemplate = (info: { seriesName: string; value: number }) => {
    return formatMeasure(
      values.find((v) => v.config.guid === info.seriesName),
      info.value
    );
  };

  return {
    enabled: settings.showTooltip,
    contentTemplate,
  };
}

function defaultValueAxis(settingsArea: ChartStyleSettings): Array<DxValueAxis> {
  const linearSettings = getLinearSettings(settingsArea);
  if (linearSettings === undefined) {
    return [];
  }
  const rotated = linearSettings.rotated === true;
  const valueAxes = updateValueAxis(linearSettings)?.map(
    (va): DxValueAxis => ({
      name: va.name,
      visible: true,
      title: { text: va.title, font: { family: "Roboto" } },
      position: va.name === "Secondary" ? (rotated ? "top" : "right") : rotated ? "bottom" : "left",
      label: { visible: va.showLabel !== false, font: { family: "Roboto" } },
      grid: getValueAxisGrid(settingsArea),
    })
  );

  return valueAxes || [];
}
